import { useDisclosure } from "@chakra-ui/react";
import { Spinner } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  useCreateProjectMutation,
  useGetProjectCreateDataQuery,
} from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { withCan } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { omit } from "lodash";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, {
  type FC,
  type FormEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import { FormProjectCmp } from "../common/project-form/FormProjectCmp";
import {
  FormProjectCtx,
  StoreProjectForm,
} from "../common/project-form/StoreProjectForm";

const PageProjectCreateInner = observer(function PageProjectCreate() {
  const { workspaceStore } = useStore();
  const store = useContext(FormProjectCtx);
  const router = useRouter();

  useEffect(() => {
    if (router.query.brand_id && typeof router.query.brand_id === "string") {
      store.form.brand_id.onChange(router.query.brand_id);
    }
  }, [router.query]);

  useEffect(() => {
    if (
      !router.query.fromTemplateId ||
      typeof router.query.fromTemplateId !== "string" ||
      router.query.fromTemplateId.length === 0
    )
      return;

    store.fetchProjectTemplate(router.query.fromTemplateId);
  }, [router.query]);

  useEffect(() => {
    if (!store.isTemplate) return;
    store.form.brand_id.onChange(
      workspaceStore.settings?.brandTemplateId ?? "",
    );
  }, [store.isTemplate]);

  const [createProject, createProjectState] = useCreateProjectMutation({
    onCompleted(data) {
      if (data?.createProject.id) {
        router.push({
          pathname: "/projects/detail",
          query: { id: data.createProject.id },
        });
      }
    },
    ...onError(),
  });

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (store.isFromTemplate) {
      await store.createProjectFromTemplate();
    } else {
      const payload = await store.form.validate();
      if (payload === null) return;

      await createProject({
        variables: { input: { ...omit(payload, "code") } },
      });
    }
  };

  const cancelModal = useDisclosure();

  return (
    <React.Fragment>
      {createProjectState.loading && <Spinner absolute />}

      <FormProjectCmp
        onSubmit={onSubmit}
        onCancel={cancelModal.onOpen}
        loading={createProjectState.loading}
        submitTitle={t`Create project`}
        isTemplate={store.isTemplate}
      />

      <ModalConfirm {...cancelModal} onConfirm={() => router.push(`/projects`)}>
        <Trans>Are you sure to cancel creating new project?</Trans>
      </ModalConfirm>
    </React.Fragment>
  );
});

function useData(store: StoreProjectForm) {
  const { loading, error, data, refetch } = useGetProjectCreateDataQuery({
    onCompleted(data) {
      store.fromGetCreateProjectDataQuery(data);
    },
  });

  store.refetchProjectCreateData = refetch;

  return {
    loading,
    error,
    data,
  };
}

export const PageProjectCreate: FC<React.PropsWithChildren<unknown>> = withCan(
  { do: ["project_create_own", "project_create_all", "project_create_team"] },
  observer(function PageCreate() {
    const [store] = useState(() => {
      const store = StoreProjectForm.create();
      // @ts-expect-error
      globalThis.store = store;
      return store;
    });
    const { loading } = useData(store);

    return (
      <FormProjectCtx.Provider value={store}>
        {loading && <Spinner absolute />}
        <PageProjectCreateInner />
      </FormProjectCtx.Provider>
    );
  }),
);
